/**
 * Admin API - All admin-only endpoints
 * Extracted from backendService.ts to improve modularity
 */

import { logInfo, logAPIError } from '../../utils/errorLogger'
import type {
  DoctorListResponse,
  FarmListResponse,
  PendingFarmVerificationsResponse
} from '../types'

/**
 * List all doctors (admin only)
 * @param page - Page number (1-indexed)
 * @param limit - Number of doctors per page
 * @param search - Search term for name, username, or email
 * @param sortBy - Field to sort by (name, username, balance, created_at)
 * @param sortOrder - Sort order (asc, desc)
 * @param activeOnly - Filter to show only active doctors
 */
export async function listAllDoctors(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  page: number = 1,
  limit: number = 20,
  search?: string,
  sortBy: string = 'name',
  sortOrder: 'asc' | 'desc' = 'asc',
  activeOnly: boolean = false
): Promise<DoctorListResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort_by: sortBy,
      sort_order: sortOrder,
      active_only: activeOnly.toString()
    })

    if (search) {
      params.append('search', search)
    }

    const url = `/api/admin/doctors?${params.toString()}`
    logInfo(`Fetching all doctors: page=${page}, limit=${limit}, search=${search || 'none'}`, 'Backend')

    const response = await makeRequest<DoctorListResponse>(url, {
      method: 'GET'
    })

    logInfo(`Fetched ${response.doctors.length} doctors (total: ${response.totalCount})`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch doctors'
    logAPIError('/api/admin/doctors', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * List all farms (admin only)
 * @param page - Page number (1-indexed)
 * @param limit - Number of farms per page
 * @param search - Search term for business name, owner name, or email
 * @param sortBy - Field to sort by (business_name, owner_name, created_date, verification_status)
 * @param sortOrder - Sort order (asc, desc)
 * @param activeOnly - Filter to show only active farms
 */
export async function listAllFarms(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  page: number = 1,
  limit: number = 20,
  search?: string,
  sortBy: string = 'business_name',
  sortOrder: 'asc' | 'desc' = 'asc',
  activeOnly: boolean = false
): Promise<FarmListResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort_by: sortBy,
      sort_order: sortOrder,
      active_only: activeOnly.toString()
    })

    if (search) {
      params.append('search', search)
    }

    const url = `/api/admin/farms?${params.toString()}`
    logInfo(`Fetching all farms: page=${page}, limit=${limit}, search=${search || 'none'}`, 'Backend')

    const response = await makeRequest<FarmListResponse>(url, {
      method: 'GET'
    })

    logInfo(`Fetched ${response.farms.length} farms (total: ${response.totalCount})`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch farms'
    logAPIError('/api/admin/farms', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * List farms pending verification review (admin only)
 */
export async function listPendingFarmVerifications(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>
): Promise<PendingFarmVerificationsResponse> {
  try {
    const result = await makeRequest<PendingFarmVerificationsResponse>(
      '/api/admin/farms/verifications/pending',
      { method: 'GET' }
    )
    logInfo(`Fetched ${result.farms.length} pending farm verifications`, 'Backend')
    return result
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch pending verifications'
    logAPIError('/api/admin/farms/verifications/pending', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Update a farm's verification status (admin only)
 */
export async function updateFarmVerificationStatus(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  farmId: string,
  status: string,
  notes?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const result = await makeRequest<{ success: boolean; message: string }>(
      `/api/admin/farms/${farmId}/verification-status`,
      {
        method: 'PUT',
        body: JSON.stringify({ status, notes: notes || null })
      }
    )
    logInfo(`Farm ${farmId} verification status updated to ${status}`, 'Backend')
    return result
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update farm verification status'
    logAPIError(`/api/admin/farms/${farmId}/verification-status`, 'PUT', 0, errorMessage, { status })
    throw error
  }
}

/**
 * V2 insole QA log (admin-only).
 */
export interface V2QARunSummary {
  id: string
  created_at: string
  patient_id?: string | null
  doctor_id?: string | null
  verdict: string
  metrics?: Record<string, unknown>
  stl_quality?: Record<string, unknown>
  refine_report?: Record<string, unknown>
}

export interface V2QARunDetail extends V2QARunSummary {
  session_id?: string
  mesh_id?: string
  project_id?: string | null
  params?: Record<string, unknown>
  raw_stl_file_id?: string | null
  refined_stl_file_id?: string | null
  render_paths?: Record<string, string>
  notes?: string | null
}

export interface V2QARunListResponse {
  runs: V2QARunSummary[]
  total?: number
}

export async function listV2QARuns(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  opts: { limit?: number; offset?: number; patient_id?: string } = {}
): Promise<V2QARunListResponse> {
  const params = new URLSearchParams()
  if (opts.limit != null) params.append('limit', String(opts.limit))
  if (opts.offset != null) params.append('offset', String(opts.offset))
  if (opts.patient_id) params.append('patient_id', opts.patient_id)
  const qs = params.toString()
  const url = `/api/admin/v2/qa/runs${qs ? '?' + qs : ''}`
  return makeRequest<V2QARunListResponse>(url, { method: 'GET' })
}

export async function getV2QARun(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  runId: string
): Promise<V2QARunDetail> {
  return makeRequest<V2QARunDetail>(`/api/admin/v2/qa/runs/${runId}`, { method: 'GET' })
}

/**
 * Delete a doctor account permanently (admin only, requires admin password)
 */
export async function deleteDoctorAccount(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string,
  adminPassword: string
): Promise<{ ok: boolean; doctorId: string; name: string; username: string; message: string }> {
  try {
    const result = await makeRequest<{ ok: boolean; doctorId: string; name: string; username: string; message: string }>(
      `/api/admin/doctors/${doctorId}`,
      {
        method: 'DELETE',
        body: JSON.stringify({ admin_password: adminPassword })
      }
    )
    logInfo(`Deleted doctor account: ${doctorId}`, 'Backend')
    return result
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete doctor account'
    logAPIError(`/api/admin/doctors/${doctorId}`, 'DELETE', 0, errorMessage)
    throw error
  }
}

/**
 * Delete a farm account permanently (admin only, requires admin password)
 */
export async function deleteFarmAccount(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  farmId: string,
  adminPassword: string
): Promise<{ ok: boolean; farmId: string; businessName: string; message: string }> {
  try {
    const result = await makeRequest<{ ok: boolean; farmId: string; businessName: string; message: string }>(
      `/api/admin/farms/${farmId}`,
      {
        method: 'DELETE',
        body: JSON.stringify({ admin_password: adminPassword })
      }
    )
    logInfo(`Deleted farm account: ${farmId}`, 'Backend')
    return result
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete farm account'
    logAPIError(`/api/admin/farms/${farmId}`, 'DELETE', 0, errorMessage)
    throw error
  }
}

